import { useBundleLabel } from '@hooks/useBundleLabel'
import { getCommonGaDto } from '@utils/getCommonGaDto'
import {
  getCustomAttrCustomFacetCampaign,
  getCustomAttrCustomFacetLabel,
} from '@utils/getCustomAttrCustomFacetCampaign'
import { getFullImageUrl } from '@utils/getFullImageUrl'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CwsA,
  CwsButton,
  CwsCounter,
  CwsDivider,
  CwsAlert,
  CwsGridCol,
  CwsGridRow,
  CwsP,
  CwsGrid,
  CwsSection,
} from '@components/cws'
import ImagePlaceholder from '@components/product/ProductImage/assets/placeholder.svg'
import type { WishlistItem as WishlistItemType } from '@model/wishlist/WishlistItem'
import Link from 'next/link'
import { PriceLine } from '@components/product'
import { getFormattedPrice } from '@utils/price'
import { CartAction } from '@components/cart/CartAction'
import { getSlug } from '@utils/slugs'
import { oncePerBasket } from '@utils/product-utils'
import { enhanceOriginalProduct } from '@utils/product/product-info'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS } from '@model/locales'
import { useRouter } from 'next/router'
import { useTranslate } from '@hooks/useTranslation'
import { useCurrency } from '@hooks/useCurrency'
import { useWishlist } from '@hooks/useWishlist'
import { useMediaLess } from '@ui/hooks/useMediaLess'
import { MOBILE_BP } from '@app/constants'
import { ProductTagsRow } from '@components/product/ProductCard'
import { getFreeProductInfo } from '@utils/product/old-price-utils'
import { useCart } from '@hooks/useCart'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import { AppRoutes } from '@app/routes'
import type { BundleGroups } from '@components/profile/Wishlist/Wishlist'
import * as R from 'ramda'
import Tooltip from '../../../../ui/components/Tooltip/Tooltip'
import { Product } from '@model/product'
import { gaEventCart } from '@lib/gtm'
import { useAppState } from '@context/state'
import { useInitialData } from '@context/initialDataContext'
import { isAlcoholicProduct } from '@utils/product/isAlcoholicProduct'
import { getFirstCloudinaryImage, hasCloudinaryImages } from '@utils/product/cloudinary-images-utils'
import { TRANSFORMATIONS } from '@components/common/UniversalImage/UniversalImage'
import { UniversalImage } from '@components/common/UniversalImage'
import { useCartItemPrice } from '@hooks/useCartItemPrice'

interface WishlistItemProps {
  item: WishlistItemType
  isPrint?: boolean
  bundleGroups?: BundleGroups
  quantityProductIsFree?: number
  productIndex?: number
}

export const WishlistItem: FC<WishlistItemProps> = ({
  item,
  isPrint,
  bundleGroups,
  quantityProductIsFree = 0,
  productIndex = 0,
}) => {
  const { removeWishlistItem, updateWishlistItemQuantity } = useWishlist()
  const isMobile = useMediaLess(MOBILE_BP)
  const {
    locale,
    query: { catalogId },
  } = useRouter()
  const {
    state: { customer },
  } = useAppState()
  const {
    initialData: { allPromotions },
  } = useInitialData()
  const { translate } = useTranslate()
  const { currency } = useCurrency()

  const productSlug = getSlug(item.product?.name, item.product?.sku)
  const isFreeProduct = useMemo(() => item.product.sku.startsWith('GWP-'), [item])
  const isGiftBoxOrGalaProduct = useMemo(() => oncePerBasket(item.product.sku), [item])
  const { isQuantityLow, maxQuantity } = useMemo(() => {
    const { info, quantity } = enhanceOriginalProduct(item.product, KK_LANG_IDS[locale as Locale], allPromotions)
    return {
      isQuantityLow: info?.isLowStock || false,
      maxQuantity: quantity || item.product.quantity,
    }
  }, [item, allPromotions, locale])

  const [localQuantity, setLocalQuantity] = useState(item.quantityDesired ?? 0)
  const [imgSrcError, setImgSrcError] = useState<boolean>(false)
  const {
    priceInfo,
    rowFullPrice: rowRealPrice,
    basePrice,
    normalPrice,
    topPrice,
    isClubOnePrice,
  } = useCartItemPrice({
    product: item.product,
    quantity: localQuantity,
    currency,
    bundleGroups,
    quantityProductIsFree,
  })

  useEffect(() => {
    setLocalQuantity(item.quantityDesired ?? 0)
  }, [item.quantityDesired])

  const isAlcoholItem = isAlcoholicProduct(item.product.custom3)
  const isBundleApplied = useMemo(() => {
    if (!priceInfo.bundleLabel) return false

    if (priceInfo.bundleLabel.includes('bundle') && bundleGroups && priceInfo.bundleLabel in bundleGroups) {
      const sum = R.compose(R.sum, R.map(R.prop('quantity')))(bundleGroups[priceInfo.bundleLabel])
      const numberOfBundles = Math.floor(sum / priceInfo.bundleQuantity)

      if (numberOfBundles >= 1) return true
    }
    return false
  }, [priceInfo, bundleGroups])

  const priceDifference = normalPrice !== basePrice
  const shouldShowDiscountLabel = isAlcoholItem ? false : priceDifference
  const shouldShowAlcoNoticePrice = isAlcoholItem && priceDifference

  const rowTotalPrice = useMemo(() => {
    if (!priceInfo.bundleQuantity) return basePrice * localQuantity
    return priceInfo.topPrice * localQuantity
  }, [priceInfo.bundleQuantity, priceInfo.topPrice, basePrice, localQuantity])

  const { promotionId: giftPromotionId } = getFreeProductInfo(item.product)

  const campaign = getCustomAttrCustomFacetCampaign(item.product.customAttrArray)
  const label = getCustomAttrCustomFacetLabel(item.product.customAttrArray)
  const { bundleType = '', bundlePrice } = priceInfo
  const isActiveCampaign = !isGloballyPricelist()
    ? campaign && !!campaign.length
    : campaign && !!campaign.length && (basePrice !== normalPrice || !!bundlePrice)

  const bundleLabel = useBundleLabel(priceInfo, campaign, translate)

  const productLinkHref = catalogId
    ? `/${catalogId}${AppRoutes.productDetails.get(productSlug)}`
    : AppRoutes.productDetails.get(productSlug)

  const removeHandler = useCallback(async () => {
    await removeWishlistItem(item.id)
  }, [item, removeWishlistItem])

  const { addToCartWithSideEffects } = useCart('shopping cart')

  const discountValue = useMemo(() => {
    if (priceInfo.bundleType && priceInfo.bundleSavePercent && localQuantity >= priceInfo.bundleQuantity) {
      return `-${priceInfo.bundleSavePercent}%`
    } else if (isBundleApplied) {
      return `-${priceInfo.bundleSavePercent}%`
    }

    return priceInfo.youSavePercent ? `-${priceInfo.youSavePercent}%` : ''
  }, [priceInfo, localQuantity, isBundleApplied])
  const gaEventAddToCart = (product: Product, quantity: number) => {
    const commonDto = getCommonGaDto(product, allPromotions, bundleLabel, translate)
    const productUrlArr = ['', ''] //product.url.split('/')
    const discount = normalPrice - basePrice
    const category = productUrlArr[1]
    const category2 = productUrlArr[2]
    const item = {
      ...commonDto,
      price: normalPrice.toString(),
      discount: discount ? (normalPrice - basePrice).toFixed(2).toString() : '',
      currency: currency,
      item_category: category ?? '',
      item_category2: category2 ?? '',
      item_list_id: 'wishlist_page',
      index: productIndex + 1,
      quantity: quantity.toString(),
    }

    gaEventCart('add_to_cart', item, customer, basePrice || normalPrice)
  }
  const handleAddToCart = useCallback(async () => {
    try {
      await addToCartWithSideEffects({ productId: item.product.id, sku: item.sku, quantity: localQuantity })
      await removeWishlistItem(item.id)
      gaEventAddToCart(item.product, localQuantity)
    } catch (e) {
      console.error(`Error adding ${item.sku} to cart`, e)
    }
  }, [localQuantity, addToCartWithSideEffects, item, locale, currency])

  const counter = isPrint ? (
    <div className="flex justify-center">
      <CwsP>{localQuantity}</CwsP>
    </div>
  ) : (
    !isFreeProduct && (
      <>
        <CwsCounter
          insert={!!item.finalPriceExTax}
          value={localQuantity}
          disabled={!item.finalPriceExTax}
          min={0}
          max={isGiftBoxOrGalaProduct ? 1 : maxQuantity}
          onChange={async (value: number) => {
            const numVal = +value
            setLocalQuantity(numVal)
            await updateWishlistItemQuantity(item.id, numVal)
          }}
        />
        {isQuantityLow && (
          <div className="flex flex-row space-x-sm">
            <CwsAlert variant="inline" className="cws-justify-content-center" type="info">
              <CwsP size="small">{translate('product.detail.limited.quantity')}</CwsP>
            </CwsAlert>
          </div>
        )}
      </>
    )
  )

  const bundlePriceNode = (
    <Tooltip
      position="top"
      contentCenter="center"
      noticeBorder
      content={
        <span className="text-left">
          <CwsP hideSpacing size="small">
            {translate('product.tile.bundle.products', { bundle: priceInfo.bundleQuantity })}
          </CwsP>
        </span>
      }
    >
      <CwsP hideSpacing weight="500" className="cursor-pointer" color={quantityProductIsFree ? 'notice' : 'midnight'}>
        {getFormattedPrice(rowRealPrice, currency)}
      </CwsP>
    </Tooltip>
  )

  const MobileProductsList = () => {
    return (
      <>
        <CwsGridRow noGutter>
          <CwsGridCol col="12" lg="fluid" className="mt-2 lg:mt-0">
            <CwsGridRow noGutter verticalAlign="center">
              <CwsGridCol col="3" className="mr-4 lg:mr-8 flex md:justify-start">
                {imgSrcError && <ImagePlaceholder width="75" height="80" className="object-contain" />}
                {!imgSrcError && item.product.image && (
                  <Link href={productLinkHref} passHref>
                    <UniversalImage
                      provider={hasCloudinaryImages(item.product) ? 'cloudinary' : 'next'}
                      alt={item.product.manufacturerName}
                      height="80"
                      width="80"
                      className="object-contain aspect-[1/1] cursor-pointer hover:opacity-75"
                      src={
                        hasCloudinaryImages(item.product)
                          ? getFirstCloudinaryImage(item.product) || getFullImageUrl(item.product.image)
                          : getFullImageUrl(item.product.image)
                      }
                      onError={setImgSrcError.bind(this, true)}
                      namedTransformations={TRANSFORMATIONS['small']}
                    />
                  </Link>
                )}
              </CwsGridCol>
              <CwsGridCol col="fluid" className="cws-header__shopping-list-item-description">
                <CwsSection variant="div">
                  <ProductTagsRow
                    giftPromotionId={giftPromotionId}
                    campaign={isActiveCampaign ? campaign : undefined}
                    label={label}
                    bundleLabel={bundleLabel}
                    bundleType={bundleType}
                    promotionResults={item.product.promotionResults}
                  />
                </CwsSection>
                <Link href={productLinkHref} passHref legacyBehavior>
                  <CwsA hideArrow size="small">
                    {item.product.name}
                  </CwsA>
                </Link>
                <div className="flex">
                  <CwsP size="small">{translate('product.detail.sku')}:&nbsp;</CwsP>
                  <CwsP size="small">{item.product.sku}</CwsP>
                </div>
              </CwsGridCol>
            </CwsGridRow>
            <div className="cws-mt-xs" />
            <CwsGridRow noGutter verticalAlign="center">
              <CwsGridCol col="4" className="text-start">
                <CwsP size="small" color="midnight">
                  {translate('cart.list.price')}:
                </CwsP>
              </CwsGridCol>
              <CwsGridCol col="4" className="text-center">
                <CwsP size="small" color="midnight">
                  {translate('cart.list.quantity')}:
                </CwsP>
              </CwsGridCol>
              <CwsGridCol col="4" className="text-end">
                <CwsP size="small" color="midnight">
                  {translate('cart.list.total')}:
                </CwsP>
              </CwsGridCol>
            </CwsGridRow>
            <CwsDivider spacing="xs" variant="dashed" />
            <CwsGridRow noGutter>
              <CwsGridCol col="4" className="flex justify-start items-center">
                {shouldShowDiscountLabel ? (
                  <div className="flex flex-col gap-y-2 items-start">
                    <div className="flex flex-col gap-x-2 items-baseline">
                      <PriceLine size="sm" label="" value={basePrice} valueColor="pink" discountValue={discountValue} />
                    </div>
                    <span className="flex justify-end">
                      <span className="flex flex-col ">
                        <CwsP size="small" color="dark-grey" className="whitespace-nowrap">
                          {translate('w2.product.title.regular')}:
                        </CwsP>
                        <CwsP size="small" weight="700" className="cws-m-0" color="dark-grey">
                          {getFormattedPrice(normalPrice, currency)}
                        </CwsP>
                      </span>
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-y-2 items-center">
                    {shouldShowAlcoNoticePrice ? (
                      <CwsGrid className="cws-py-xs" width="fluid">
                        <PriceLine size="sm" label="" value={basePrice} valueColor="pink" />
                      </CwsGrid>
                    ) : (
                      <CwsGrid className="cws-py-xs" width="fluid">
                        <PriceLine size="sm" label="" value={normalPrice} />
                      </CwsGrid>
                    )}
                  </div>
                )}
              </CwsGridCol>

              <CwsGridCol col={4} className="flex flex-col justify-center items-center">
                {counter}
              </CwsGridCol>

              <CwsGridCol className="text-end flex flex-col justify-center items-end" col="4">
                <CwsP hideSpacing weight="500" color={quantityProductIsFree ? 'notice' : 'midnight'}>
                  {getFormattedPrice(rowRealPrice, currency)}
                </CwsP>
                {Boolean(quantityProductIsFree) && (
                  <span className="flex flex-wrap justify-end">
                    <CwsP hideSpacing size="small" color="dark-grey">
                      {translate('w2.product.title.regular')}:
                    </CwsP>
                    <CwsP hideSpacing size="small" className="cws-m-0" color="dark-grey">
                      {getFormattedPrice(rowTotalPrice, currency)}
                    </CwsP>
                  </span>
                )}
              </CwsGridCol>
              {localQuantity === 0 && (
                <CwsGridCol col="12" className="flex flex-col justify-center ">
                  <CwsDivider spacing="xs" variant="dashed" />
                  <section className="flex justify-center">
                    <CartAction icon="bin" variant="error" size="16px" onClick={removeHandler}>
                      {translate('w2.cart.product.remove')}
                    </CartAction>
                  </section>
                </CwsGridCol>
              )}
              {!isGloballyPricelist() && (
                <CwsGridCol col="12" className="flex flex-col justify-center">
                  <CwsDivider spacing="xs" variant="dashed" />
                  <section className="flex justify-center  cws-py-1.6  ">
                    <CwsButton onClick={handleAddToCart} variant="secondary">
                      {translate('product.tile.add.to.shopping.cart')}
                    </CwsButton>
                  </section>
                </CwsGridCol>
              )}
            </CwsGridRow>
          </CwsGridCol>
        </CwsGridRow>
      </>
    )
  }
  const orderProductTotalPriceNode = (
    <CwsGridCol className="text-end flex flex-col justify-end items-end print:max-w-[50px]" col={isPrint ? '1' : '3'}>
      {quantityProductIsFree ? (
        bundlePriceNode
      ) : (
        <CwsP hideSpacing weight="500">
          {getFormattedPrice(rowRealPrice, currency)}
        </CwsP>
      )}

      {Boolean(quantityProductIsFree) && (
        <span className="flex flex-row">
          <CwsP hideSpacing size="small" color="dark-grey" className="whitespace-nowrap">
            {translate('w2.product.title.regular')}:&nbsp;
          </CwsP>
          <CwsP hideSpacing size="small" className="cws-m-0" color="dark-grey">
            {getFormattedPrice(rowTotalPrice, currency)}
          </CwsP>
        </span>
      )}
    </CwsGridCol>
  )

  return (
    <>
      {isMobile ? (
        <MobileProductsList />
      ) : (
        <CwsGridRow noGutter verticalAlign="center">
          <CwsGridCol col="1" lg="1" className="mr-4 lg:mr-8 flex md:justify-start print:mr-[-60px]">
            {imgSrcError && <ImagePlaceholder width="75" height="80" className="object-contain" />}
            {!imgSrcError && item.product.image && (
              <Link href={productLinkHref} passHref>
                <UniversalImage
                  provider={hasCloudinaryImages(item.product) ? 'cloudinary' : 'next'}
                  alt={item.product.manufacturerName}
                  height="80"
                  width="80"
                  className="object-contain aspect-[1/1] cursor-pointer hover:opacity-75"
                  src={
                    hasCloudinaryImages(item.product)
                      ? getFirstCloudinaryImage(item.product) || getFullImageUrl(item.product.image)
                      : getFullImageUrl(item.product.image)
                  }
                  onError={setImgSrcError.bind(this, true)}
                  namedTransformations={TRANSFORMATIONS['small']}
                />
              </Link>
            )}
          </CwsGridCol>
          <CwsGridCol
            col="5"
            lg="5"
            className="cws-header__shopping-list-item-description flex flex-col gap-2 print:w-max-[100px]"
          >
            <CwsSection variant="div">
              <ProductTagsRow
                giftPromotionId={giftPromotionId}
                campaign={isActiveCampaign ? campaign : undefined}
                label={label}
                bundleLabel={bundleLabel}
                bundleType={bundleType}
                promotionResults={item.product.promotionResults}
              />
            </CwsSection>
            <Link href={productLinkHref} passHref legacyBehavior>
              <CwsA hideArrow size="small">
                {item.product.name}
              </CwsA>
            </Link>
            <div className="flex">
              <CwsP size="small">{translate('product.detail.sku')}:&nbsp;</CwsP>
              <CwsP size="small">{item.product.sku}</CwsP>
            </div>
            {!isPrint && !isGloballyPricelist() && (
              <CwsButton onClick={handleAddToCart} variant="secondary">
                {translate('product.tile.add.to.shopping.cart')}
              </CwsButton>
            )}
          </CwsGridCol>
          <CwsGridCol col="fluid" lg="fluid" className="mt-2 lg:mt-0">
            <CwsGridRow justifyContent="space-between" noGutter verticalAlign="center">
              <CwsGridCol className="text-center" col={isPrint ? '5' : '3'}>
                {shouldShowDiscountLabel ? (
                  <div className="flex flex-col gap-y-2 items-center">
                    <div className="flex gap-x-2 items-baseline">
                      <CwsGrid className="cws-py-xs" width="fluid">
                        <PriceLine
                          size="sm"
                          label=""
                          value={basePrice}
                          valueColor="pink"
                          discountValue={discountValue}
                        />
                      </CwsGrid>
                    </div>
                    <span className="flex justify-end">
                      <span className="flex gap-2">
                        <CwsP size="small" color="dark-grey" className="whitespace-nowrap">
                          {translate('w2.product.title.regular')}:
                        </CwsP>
                        <CwsP size="small" weight="700" className="cws-m-0" color="dark-grey">
                          {getFormattedPrice(normalPrice, currency)}
                        </CwsP>
                      </span>
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-y-2 items-center">
                    {shouldShowAlcoNoticePrice ? (
                      <CwsGrid className="cws-py-xs" width="fluid">
                        <PriceLine size="sm" label="" value={basePrice} valueColor="pink" />
                      </CwsGrid>
                    ) : (
                      <CwsGrid className="cws-py-xs" width="fluid">
                        <PriceLine size="sm" label="" value={normalPrice} />
                      </CwsGrid>
                    )}
                  </div>
                )}
              </CwsGridCol>
              <CwsGridCol className="flex flex-col md:items-center gap-y-2 text-start" col={isPrint ? '1' : '5'}>
                {counter}
              </CwsGridCol>
              {+localQuantity !== 0 && orderProductTotalPriceNode}
              {+localQuantity === 0 && (
                <CwsGridCol className="cws-ml-xs" col="3">
                  <CartAction icon="bin" variant="error" size="16px" onClick={removeHandler}>
                    {translate('w2.cart.product.remove')}
                  </CartAction>
                </CwsGridCol>
              )}
            </CwsGridRow>
          </CwsGridCol>
        </CwsGridRow>
      )}
    </>
  )
}
